import { AudioProxyClient } from './client';
import { AudioServiceConfig, StreamInfo } from './types';

export class TauriAudioService {
  private client: AudioProxyClient;
  private isTauri: boolean;
  private tauriInvoke: any;

  constructor(config?: AudioServiceConfig) {
    this.isTauri = typeof window !== 'undefined' && !!(window as any).__TAURI__;
    
    if (this.isTauri) {
      this.tauriInvoke = (window as any).__TAURI__.invoke;
    }

    this.client = new AudioProxyClient({
      proxyUrl: config?.audioOptions?.proxyUrl || 'http://localhost:3001',
      autoDetect: config?.audioOptions?.autoDetect ?? true,
      fallbackToOriginal: config?.audioOptions?.fallbackToOriginal ?? true,
      retryAttempts: config?.audioOptions?.retryAttempts || 3,
      retryDelay: config?.audioOptions?.retryDelay || 1000,
    });
  }

  public async getStreamableUrl(originalUrl: string): Promise<string> {
    if (!this.isTauri) {
      // Not in Tauri, use standard client
      return this.client.getPlayableUrl(originalUrl);
    }

    // Check if it's already a Tauri protocol URL
    if (this.isTauriProtocolUrl(originalUrl)) {
      return originalUrl;
    }

    // Handle local files with convertFileSrc
    if (!originalUrl.startsWith('http://') && !originalUrl.startsWith('https://')) {
      return (window as any).__TAURI__.convertFileSrc(originalUrl);
    }

    // For external URLs, check if we're in development
    const isDevelopment = this.isDevelopmentMode();
    
    if (isDevelopment) {
      // In development, always use proxy for external URLs
      // This bypasses WebKit codec issues
      return this.client.getPlayableUrl(originalUrl);
    }

    // In production, try direct URL first
    try {
      // You could add a Tauri command to verify URL accessibility
      if (this.tauriInvoke) {
        const streamInfo = await this.tauriInvoke('check_audio_stream', { url: originalUrl });
        if (streamInfo.status === 200) {
          return originalUrl;
        }
      }
    } catch (error) {
      console.warn('[TauriAudioService] Direct URL check failed:', error);
    }

    // Fall back to proxy if needed
    return this.client.getPlayableUrl(originalUrl);
  }

  public async getStreamInfo(url: string): Promise<StreamInfo> {
    if (this.tauriInvoke) {
      try {
        return await this.tauriInvoke('check_audio_stream', { url });
      } catch (error) {
        console.error('[TauriAudioService] Failed to get stream info:', error);
      }
    }

    return this.client.canPlayUrl(url);
  }

  public async preloadAudio(url: string): Promise<void> {
    // Implement audio preloading logic
    const playableUrl = await this.getStreamableUrl(url);
    
    if (typeof Audio !== 'undefined') {
      const audio = new Audio();
      audio.preload = 'metadata';
      audio.src = playableUrl;
    }
  }

  private isTauriProtocolUrl(url: string): boolean {
    return url.startsWith('asset://') || 
           url.startsWith('tauri://') || 
           url.startsWith('stream://') ||
           url.includes('tauri://localhost');
  }

  private isDevelopmentMode(): boolean {
    return window.location?.hostname === 'localhost' || 
           window.location?.hostname === '127.0.0.1';
  }

  public isInTauri(): boolean {
    return this.isTauri;
  }

  public async checkDependencies(): Promise<{
    ffmpeg: boolean;
    gstreamer: boolean;
    message: string;
  }> {
    if (this.tauriInvoke) {
      try {
        return await this.tauriInvoke('check_dependencies');
      } catch (error) {
        console.error('[TauriAudioService] Failed to check dependencies:', error);
      }
    }

    return {
      ffmpeg: false,
      gstreamer: false,
      message: 'Unable to check dependencies',
    };
  }
}